import { action, computed, makeObservable, observable } from "mobx";
import { DefaultPolicyResponse, PolicyResponseModel, PolicyRenewalData, PolicyRenewalSeriesDropDown } from "../helpers/policy";
import { DefaultRequestStatus, RequestStatus } from "../services/services/requestStatus";
import Validator, { ValidatedValue } from "../helpers/validator";
import { ErrorMessages } from "../helpers/errorMessage";

export interface PolicyStore {
    policyRequestStatus: RequestStatus;

    policyResponse: PolicyResponseModel;
    setPolicyResponse: (policyResponse: PolicyResponseModel) => void;

    resetStore: () => void;

    policyRenewal: PolicyRenewalData;
    setSeries: (series: string) => void;
    setNumber: (policyNumber: string) => void;
    setPeselOrRegon: (peselOrRegon: string) => void;
    isPolicyRenewal: boolean;

    authorizedPolicyRenewalOfferId: string;
    setAuthorizedPolicyRenewalOfferId: (authorizedPolicyRenewalOfferId: string) => void;
}

export class DefaultPolicyStore implements PolicyStore {
    constructor() {
        makeObservable(this);
    }
    @observable
    private _policyRequestStatus: RequestStatus = new DefaultRequestStatus();

    @computed
    public get policyRequestStatus(): RequestStatus {
        return this._policyRequestStatus;
    }

    @observable
    private _policyResponse: PolicyResponseModel = DefaultPolicyResponse;

    @computed
    public get policyResponse() {
        return this._policyResponse;
    }

    @action
    public setPolicyResponse(policyResponse: PolicyResponseModel) {
        this._policyResponse = policyResponse;
    }

    private policyNumber = new ValidatedValue<string>("", [
        { name: "policyNumberIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required }
    ]);

    @action
    public resetStore() {
        this._policyRequestStatus = new DefaultRequestStatus();
        this._policyResponse = DefaultPolicyResponse;
        this.policyNumber.value = "";
        this._policyRenewal = {
            series: PolicyRenewalSeriesDropDown[0].id,
            number: this.policyNumber,
            peselOrRegon: this.peselOrRegonValidators()
        };
    }

    @observable
    private _policyRenewal: PolicyRenewalData = {
        series: PolicyRenewalSeriesDropDown[0].id,
        number: this.policyNumber,
        peselOrRegon: this.peselOrRegonValidators()
    };

    @computed
    public get policyRenewal(): PolicyRenewalData {
        return this._policyRenewal;
    }

    @action
    public setSeries(series: string) {
        this._policyRenewal.series = series;
    }

    @action
    public setNumber(policyNumber: string) {
        this._policyRenewal.number.value = policyNumber;
    }

    @action
    public setPeselOrRegon(peselOrRegon: string) {
        this._policyRenewal.peselOrRegon.value = peselOrRegon;
    }

    @computed
    public get isPolicyRenewal(): boolean {
        return !!(this._policyRenewal.number.value && this._policyRenewal.series);
    }

    private peselOrRegonValidators() {
        return new ValidatedValue<string>("",
            [
                { name: "peselOrRegonIsNotEmpty", fn: Validator.isNotEmpty, errorMessage: ErrorMessages.required },
                {
                    name: "peselOrRegonInvalid",
                    fn: Validator.or(Validator.pesel, Validator.regon),
                    errorMessage: ErrorMessages.invalid
                }
            ]);
    }

    @observable
    private _authorizedPolicyRenewalOfferId = "";

    @computed
    public get authorizedPolicyRenewalOfferId() {
        return this._authorizedPolicyRenewalOfferId;
    }

    @action
    public setAuthorizedPolicyRenewalOfferId(authorizedPolicyRenewalOfferId: string) {
        this._authorizedPolicyRenewalOfferId = authorizedPolicyRenewalOfferId;
    }

}
